import { AUTH_API, AUTH_STORAGE_KEYS, API_VARIABLES } from '../values/auth.values';
import { LocalStorageService } from '@/services/local-storage.service';
import { RequestProvider } from '@/shared/providers/request.provider';
import { LoginParams, RegisterParams } from '../params/auth.params';
import { ErrorProvider } from '@/shared/providers/errors.provider';
import { IAuthLoginResponse } from '../interfaces/auth.interface';
import {BrowserServiceClass as BrowserService} from '@/providers/browser-utils/browser.service';
import { User } from '@/core/models/user.model';
import log from 'js-logger';
import Vue from 'vue';

class AuthServiceClass extends Vue {

  private getPublicRequestError(error: any) {
    const response = error?.data || error || {};
    return {
      ...ErrorProvider.getError(response.error, response.errors),
      status: error?.status
    };
  }

  /* GET Methods */
  public async getUserLogged(): Promise<User> {
    try {
      // Open Media access early so private resources can load as soon as the authenticated page is rendered.
      await RequestProvider.issueMediaSession().catch((error) =>
        log.warn('Media session initialization failed', { status: error?.response?.status })
      );
      const response: any[] = await RequestProvider.get(AUTH_API.USER_INFO);
      const user = response[0];
      return new User({...user, id: user.personId, userId: user.username });
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getAccessToken() : Promise<any> {
    try {
      const response: any = await RequestProvider.get(AUTH_API.GET_ACCESS_TOKEN);
      LocalStorageService.setItem(AUTH_STORAGE_KEYS.ED_JWT, response);
      return response;
    } catch(error) { 
      throw ErrorProvider.getError(error.error);
    }
  }

  public async validateUserRegister(data: RegisterParams): Promise<any> {
    try {
      return await RequestProvider.get(AUTH_API.VALIDATE_USER_REGISTER);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async existsUsername(username:string): Promise<any> {
    try {
      let url = AUTH_API.EXISTS_USERNAME;
      url = url.replace(API_VARIABLES.USERNAME, username);

      return await RequestProvider.requestPublic('GET', url);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getTerms(): Promise<any> {
    try {
      return RequestProvider.requestPublic('GET', AUTH_API.GET_TERMS);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async validateZipcode(zipCode): Promise<any> {
    try {
      return RequestProvider.requestPublic('GET', AUTH_API.VALIDATE_ZIPCODE, null, {zipCode});
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getCountries(): Promise<any> {
    try {
      return RequestProvider.requestPublic('GET', AUTH_API.GET_COUNTRIES);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getCategories(): Promise<any> {
    try {
      return RequestProvider.requestPublic('GET', AUTH_API.GET_CATEGORIES);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getInstitutionTypes(): Promise<any> {
    try {
      return RequestProvider.requestPublic('GET', AUTH_API.GET_INSTITUTION_TYPES);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async validateInstitutionName(name): Promise<any> {
    try {
      return RequestProvider.requestPublic('GET', AUTH_API.VALIDATE_INSTITUTION_NAME, null, {name});
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  /* POST Methods */
  public async login(data: LoginParams) {
    try {
      const body = new URLSearchParams();
      body.append('grant_type', 'password');
      body.append('username', data.username);
      body.append('password', data.password);
      const response: IAuthLoginResponse = await RequestProvider.requestAuth(
        'POST',
        AUTH_API.LOGIN,
        body.toString(),
        {},
        { 'Content-Type': 'application/x-www-form-urlencoded' }
      );
      LocalStorageService.setItem(AUTH_STORAGE_KEYS.ED_JWT, response.access_token);
      LocalStorageService.setItem(AUTH_STORAGE_KEYS.ED_REFRESH_TOKEN, response.refresh_token);
      // Login creates the browser Media cookie before the application starts rendering private resources.
      await RequestProvider.issueMediaSession().catch((error) =>
        log.warn('Media session initialization failed', { status: error?.response?.status })
      );
      return response;
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async resetPasswordRequest(user: string): Promise<void> {
    try {
      return RequestProvider.requestPublic('POST', AUTH_API.PASS_RESET_REQUEST, {userIdentifier: user});
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async validateCode( id: number, code: string): Promise<void> {
    try {
      let body = { verificationId:id, code:code }
      return RequestProvider.requestPublic('POST', AUTH_API.CODE_VALIDATE, body);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async createUser(data: RegisterParams): Promise<any> {
    try {
      return await RequestProvider.requestPublic('POST', AUTH_API.CREATE_USER, data);
    } catch(error) {
      throw this.getPublicRequestError(error);
    }
  }

  public async sendPhoneCode(phoneNumber): Promise<any> {
    try {
      return await RequestProvider.requestPublic('POST', AUTH_API.SEND_PHONE_CODE, {phoneNumber});
    } catch(error) {
      throw this.getPublicRequestError(error);
    }
  }

  public async resendPhoneCode(verificationId:number): Promise<any> {
    try {
      return await RequestProvider.requestPublic('POST', AUTH_API.RESEND_PHONE_CODE, null, {verificationId});
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async validatePhoneCode(code, verificationId): Promise<any> {
    try {
      return await RequestProvider.requestPublic('POST', AUTH_API.VALIDATE_PHONE_CODE, {code, verificationId});
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async existsPhoneNumber(phoneNumber:string): Promise<any> {
    try {
      const response = await RequestProvider.requestPublic('POST', AUTH_API.EXISTS_PHONE_NUMBER, {value: phoneNumber});
      if (response.available) throw new Error('Phone number is available');
      return response;
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async existsEmail(email:string): Promise<any> {
    try {
      const response = await RequestProvider.requestPublic('POST', AUTH_API.EXISTS_EMAIL, {value: email});
      if (response.available) throw new Error('Email is available');
      return response;
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async validateEmailCode(code, verificationId): Promise<any> {
    try {
      return await RequestProvider.requestPublic('POST', AUTH_API.VALIDATE_EMAIL_CODE, {code, verificationId});
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async acceptRejectInvitation(invitationId, status): Promise<any> {
    try {
      let data = {
        invitationId,
        status
      }
      let req = AUTH_API.RESPOND_INVITATION;
      return await RequestProvider.requestPublic('POST', req, data)
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async respondInvitation(invitationId, status) {
    try {
       let data = {
        hash: invitationId,
        status
      }
      return await RequestProvider.requestPublic('POST', AUTH_API.RESPOND_INVITATION, null, data)
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async respondSubstituteInvitation(invitationId, status) {
    try {
       let data = {
        hash: invitationId,
        status
      }
      return await RequestProvider.requestPublic('POST', AUTH_API.RESPOND_SUBSTITUTE_INVITATION, data)
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  /* PUT Methods */
  public async resetPassword( newPassword:string, username:string, verificationId:string): Promise<void> {
    try {
      let resetUrl = AUTH_API.PASS_RESET.replace('verificationId',verificationId);
      let body = { newPassword:newPassword, username:username };
      return RequestProvider.requestPublic('PUT', resetUrl, body);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async changePassword( oldPassword:string, newPassword:string, username:string): Promise<void> {
    try {
      let body = { oldPassword:oldPassword, newPassword:newPassword, username:username };
      return RequestProvider.put(AUTH_API.PASS_CHANGE, body);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getInvitationByHash(hash): Promise<any> {
    try {
      let req = AUTH_API.GET_INVITATION_BY_HASH.replace('{hash}', hash)
      return RequestProvider.requestPublic('GET', req);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getSubstituteInvitationByHash(hash): Promise<any> {
    try {
      return RequestProvider.requestPublic('POST', AUTH_API.GET_SUBSTITUTE_INVITATION_BY_HASH, {invitationHash: hash});
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async completeUserInvitation(data): Promise<any> {
    try {
      let req = AUTH_API.COMPLETE_USER_INVITATION;
      return RequestProvider.requestPublic('post', req, data);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async completeSubstituteTeacherInvitation(data): Promise<any> {
    try {
      let req = AUTH_API.COMPLETE_SUBSTITUTE_INVITATION;
      return RequestProvider.requestPublic('post', req, data);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async validatePublicToken(publicToken): Promise<any> {
    try {
      let req = AUTH_API.VERIFICATION_PUBLIC_TOKEN.replace('{publicToken}', publicToken)
      return await RequestProvider.requestPublic('GET', req);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public getRecentActivities(page:number=0,pageSize:number=100): Promise<any> {
      let body = {
        page,
        pageSize
      }

      return new Promise((resolve,reject) => {
        RequestProvider.get(AUTH_API.RECENT_ACTIVITY, body).then((res:any) => {
          const newContent = res.content.map(({ ip, userAgent, logDate }) => {        
            const bs = new BrowserService('undefined');
            bs.overrideUserAgent(userAgent)

            return { 'ip': ip, 'logDate':logDate, 'browser': `${bs.getBrowserName()} ${bs.getBrowserVersion()}`, 'systemInformation': `${bs.getOSName()} ${bs.getOSVersion() || ''}`}
          });

          resolve({content: newContent, number: res.number, totalPages: res.totalPages});  
        }).catch(err => {
            reject(err);
        });
      });
  }
}

export const AuthService = new AuthServiceClass();
